import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface Column {
  key: string;
  label: string;
  className?: string;
  render?: (value: any, row: any) => React.ReactNode;
}

interface ResponsiveTableProps {
  columns: Column[];
  data: any[];
  keyField: string;
  onRowClick?: (row: any) => void;
  actions?: (row: any) => React.ReactNode;
  mobileCardFields?: string[]; // Fields to show in mobile card view
}

export const ResponsiveTable: React.FC<ResponsiveTableProps> = ({
  columns,
  data,
  keyField,
  onRowClick,
  actions,
  mobileCardFields = []
}) => {
  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(new Set());

  const toggleRow = (rowKey: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowKey)) {
      newExpanded.delete(rowKey);
    } else {
      newExpanded.add(rowKey);
    }
    setExpandedRows(newExpanded);
  };

  const renderMobileCard = (row: any, index: number) => {
    const rowKey = row[keyField];
    const isExpanded = expandedRows.has(rowKey);
    
    // Primary fields to show (first 2-3 most important)
    const primaryFields = mobileCardFields.length > 0 
      ? mobileCardFields.slice(0, 3)
      : columns.slice(0, 3).map(col => col.key);
    
    // Secondary fields to show when expanded
    const secondaryFields = columns
      .filter(col => !primaryFields.includes(col.key))
      .map(col => col.key);

    return (
      <div 
        key={rowKey} 
        className={`bg-white border border-gray-200 rounded-lg p-4 mb-3 shadow-sm ${
          onRowClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''
        }`}
        onClick={() => onRowClick?.(row)}
      >
        {/* Primary Information */}
        <div className="space-y-2">
          {primaryFields.map(fieldKey => {
            const column = columns.find(col => col.key === fieldKey);
            if (!column) return null;
            
            const value = row[fieldKey];
            return (
              <div key={fieldKey} className="flex justify-between items-start">
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {column.label}
                  </span>
                  <div className="mt-1 text-sm text-gray-900 break-words">
                    {column.render ? column.render(value, row) : value}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Expandable Section */}
        {secondaryFields.length > 0 && (
          <div className="mt-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleRow(rowKey);
              }}
              className="flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              {isExpanded ? (
                <>
                  <ChevronDown className="w-4 h-4 mr-1" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronRight className="w-4 h-4 mr-1" />
                  Show More
                </>
              )}
            </button>
            
            {isExpanded && (
              <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                {secondaryFields.map(fieldKey => {
                  const column = columns.find(col => col.key === fieldKey);
                  if (!column) return null;
                  
                  const value = row[fieldKey];
                  return (
                    <div key={fieldKey} className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-medium text-gray-500">
                          {column.label}:
                        </span>
                        <span className="ml-2 text-sm text-gray-900">
                          {column.render ? column.render(value, row) : value}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        {actions && (
          <div className="mt-4 pt-3 border-t border-gray-100">
            <div className="flex flex-wrap gap-2">
              {actions(row)}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      {/* Mobile View */}
      <div className="block lg:hidden">
        {data.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No data available
          </div>
        ) : (
          <div className="space-y-3">
            {data.map((row, index) => renderMobileCard(row, index))}
          </div>
        )}
      </div>

      {/* Desktop View */}
      <div className="hidden lg:block">
        <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      column.className || ''
                    }`}
                  >
                    {column.label}
                  </th>
                ))}
                {actions && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + (actions ? 1 : 0)} className="px-6 py-8 text-center text-gray-500">
                    No data available
                  </td>
                </tr>
              ) : (
                data.map((row, index) => (
                  <tr
                    key={row[keyField]}
                    className={`${
                      onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''
                    } ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    onClick={() => onRowClick?.(row)}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${
                          column.className || ''
                        }`}
                      >
                        {column.render 
                          ? column.render(row[column.key], row) 
                          : row[column.key]
                        }
                      </td>
                    ))}
                    {actions && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          {actions(row)}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};